/**
 * Capitalizes the first letter of each word in the input string.
 *
 * @param sentence - The string to be capitalized. Default is an empty string.
 * @param options - Capitalization options
 * @param options.split - Delimiter to split words. Default is space.
 * @param options.join - Delimiter to join words. Default is space.
 * @param options.isCamel - Whether to convert to camelCase (first word lowercase). Default is false.
 * @param options.isFirstLetter - Whether to capitalize only the first letter of sentence. Default is false.
 * @returns A string with the first letter of each word capitalized (or sentence-cased when requested).
 */

export const capitalize = (sentence: string = '', { split = ' ', join = ' ', isCamel = false, isFirstLetter = false } = {}): string => {
  const splitPattern = split === ' ' ? /\s+/ : new RegExp(`[\\s${split.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]+`)

  const words = sentence.split(splitPattern)
    .filter((word) => word.length)
    .map((word, idx) => {
      const firstLetterMatch = word.match(/\p{L}/u)
      if (!firstLetterMatch) return word

      const i = firstLetterMatch.index!
      // For sentence case, only capitalise the first word; lowercase subsequent word-initial letters
      const firstChar = (isFirstLetter && idx > 0)
        ? word.charAt(i).toLowerCase()
        : word.charAt(i).toUpperCase()
      return word.slice(0, i) + firstChar + word.slice(i + 1).toLowerCase()
    })

  if (isCamel && words.length) {
    const firstLetterMatch = words[0].match(/\p{L}/u)
    if (firstLetterMatch) {
      const i = firstLetterMatch.index!
      words[0] = words[0].slice(0, i) + words[0].charAt(i).toLowerCase() + words[0].slice(i + 1)
    }
  }

  return words.join(join)
}
